import type { FontFallback } from '@pz/scene';

/**
 * The fonts a product actually draws with.
 *
 * A definition lists every font of the shop (77 on BearBox, about 14 MB of TTF),
 * because a design area's picker or a font choice may offer any of them. The
 * preview used to load them all on page load: on mobile data the Mini Box's two
 * fonts arrived behind seventy-five others, and the whole page crawled. Only
 * what the design, its questions and its bindings name is loaded up front; any
 * other font is loaded the moment a scene draws with it.
 *
 * Walked, not listed: every string anywhere in the scenes, the options (their
 * values' `meta.fontId`, a design area's font list) and the bindings that is a
 * known font id counts, so a new element type's font is never missed.
 */
export function fontIdsIn(value: unknown, known: ReadonlySet<string>, into: Set<string> = new Set()): Set<string> {
  const walk = (node: unknown): void => {
    if (typeof node === 'string') {
      if (known.has(node)) into.add(node);
    } else if (Array.isArray(node)) {
      for (const item of node) walk(item);
    } else if (node && typeof node === 'object') {
      for (const item of Object.values(node)) walk(item);
    }
  };
  walk(value);
  return into;
}

/** Adds each font's Serbian twin (drawn for Cyrillic and č ć đ š ž when the font lacks them). */
export function withFontTwins(ids: Set<string>, twins: Record<string, FontFallback> | undefined): Set<string> {
  for (const id of [...ids]) {
    const twin = twins?.[id]?.fontId;
    if (twin) ids.add(twin);
  }
  return ids;
}

export function fontsInUse(product: {
  fonts: ReadonlyArray<{ id: string }>;
  sides?: unknown;
  options?: unknown;
  bindings?: unknown;
  fontTwins?: Record<string, FontFallback>;
}): Set<string> {
  const known = new Set(product.fonts.map((font) => font.id));
  return withFontTwins(fontIdsIn([product.sides, product.options, product.bindings], known), product.fontTwins);
}
